import { Theme } from '@mui/material/styles'

import { hoverTransition, onHover, tintPlain, tintWashed } from '../interaction'

// ----------------------------------------------------------------------

export default function Table(theme: Theme) {
  const selected = theme.palette.action.selected

  return {
    MuiTableContainer: {
      styleOverrides: {
        root: {
          position: 'relative',
        },
      },
    },
    MuiTableRow: {
      styleOverrides: {
        /**
         * Every rule here is repeated onto `.MuiTableCell-root`. TableCell below
         * paints an opaque `background.paper` on each cell, so a colour set on the
         * row alone is covered by its own children and never reaches the screen.
         */
        root: {
          ...hoverTransition(theme),
          '& .MuiTableCell-root': hoverTransition(theme),
          '&.MuiTableRow-hover': onHover(
            {
              backgroundColor: theme.palette.action.hover,
              '& .MuiTableCell-root': { backgroundColor: theme.palette.action.hover },
            },
            {
              backgroundColor: 'transparent',
              '& .MuiTableCell-root': { backgroundColor: theme.palette.background.paper },
            }
          ),
          '&.Mui-selected': {
            backgroundColor: selected,
            '& .MuiTableCell-root': { backgroundColor: selected },
            // Over the cream, not instead of it: a hovered selected row that swaps
            // its tint for the wash reads as no longer selected. The cell rule is
            // nested inside the row's `:hover`, so it answers the row being hovered
            // rather than the one cell the pointer happens to be over.
            ...onHover(
              { ...tintWashed(theme, selected), '& .MuiTableCell-root': tintWashed(theme, selected) },
              { ...tintPlain(selected), '& .MuiTableCell-root': tintPlain(selected) }
            ),
          },
        },
      },
    },
    MuiTableCell: {
      styleOverrides: {
        root: {
          borderBottom: 'none',
          backgroundColor: theme.palette.background.paper,
          // Figures line up in columns and stop shifting width as values change.
          fontVariantNumeric: 'tabular-nums',
          // Lab row rhythm: 44px rows instead of MUI's 16px-padded 56px+.
          padding: '10px 16px',
        },
        // `root` above is composed after TableCell's own ownerState.size === 'small'
        // styling, so without this override the blanket padding would swallow the
        // size variant and make size="small" tables (Contacts list, import preview)
        // looser instead of tighter. This restores MUI's own small-cell padding.
        sizeSmall: {
          padding: '6px 16px',
        },
        head: {
          color: theme.palette.text.secondary,
          backgroundColor: theme.palette.background.paper,
          borderBottom: '1px solid',
          borderBottomColor: theme.palette.divider,
        },
        stickyHeader: {
          backgroundColor: theme.palette.background.paper,
          backgroundImage: `linear-gradient(to bottom, ${theme.palette.background.neutral} 0%, ${theme.palette.background.neutral} 100%)`,
        },
        paddingCheckbox: {
          paddingLeft: theme.spacing(1),
        },
      },
    },
    MuiTablePagination: {
      defaultProps: {
        // backIconButtonProps: {
        //   size: 'small',
        // },
        // nextIconButtonProps: {
        //   size: 'small',
        // },
        SelectProps: {
          MenuProps: {
            MenuListProps: {
              sx: {
                '& .MuiMenuItem-root': {
                  ...theme.typography.body2,
                },
              },
            },
          },
        },
      },

      styleOverrides: {
        root: {
          borderTop: `solid 1px ${theme.palette.divider}`,
        },
        toolbar: {
          height: 52,
        },
        actions: {
          marginRight: theme.spacing(1),
        },
        select: {
          '&:focus': {
            borderRadius: theme.shape.borderRadius,
          },
        },
      },
    },
  }
}
